/**
 * "Legacy access not connected" — and the one button that fixes it.
 *
 * Delegated mode reads the legacy CRM as the signed-in migrator, which needs a
 * one-time consent (`/api/auth/legacy/login`). That is a full-page redirect to
 * Microsoft, not an API call, so the button is a plain navigation. The
 * callback lands back here with `?legacy=connected` or `?legacy_error=...`.
 */

import { Icon, Plug } from "@norican/icons";
import { Alert, Button } from "@norican/react";
import { useSearchParams } from "react-router";

import { useT } from "@/i18n";

import { useMigrateStatus } from "../api";

export function ConnectBanner() {
  const { t } = useT("tickets");
  const status = useMigrateStatus();
  const [params] = useSearchParams();
  const justConnected = params.get("legacy") === "connected";
  const failed = params.get("legacy_error") !== null;

  if (status.data === undefined) {
    return null;
  }

  return (
    <div className="flex flex-col gap-3">
      {failed ? <Alert tone="danger">{t("migrate.connect.failed")}</Alert> : null}
      {justConnected && status.data.connected ? (
        <Alert tone="success">{t("migrate.connect.connected")}</Alert>
      ) : null}
      {status.data.connected ? null : (
        <Alert tone="warning" title={t("migrate.connect.title")}>
          <div className="flex flex-col gap-3">
            <span>
              {status.data.can_connect
                ? t("migrate.connect.body")
                : t("migrate.connect.unavailable")}
            </span>
            {status.data.detail ? (
              <span className="text-fg-muted">{status.data.detail}</span>
            ) : null}
            {status.data.connect_url ? (
              <div>
                <Button
                  variant="outline"
                  onClick={() => window.location.assign(status.data.connect_url ?? "")}
                >
                  <Icon icon={Plug} size="sm" />
                  {t("migrate.connect.button")}
                </Button>
              </div>
            ) : null}
          </div>
        </Alert>
      )}
    </div>
  );
}
